import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import "./biography.css";
import { useNavigate } from "react-router-dom";
import * as flags from "country-flag-icons/react/3x2";
import { HiOutlineMapPin } from "react-icons/hi2";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import toast from "react-hot-toast";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
  getCountries,
  getExampleNumber,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.full.json";
import { REST_API_URL } from "../../api/config";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const redIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  iconRetinaUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, extra = {}) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "User-Timezone": getUserTimeZone(),
  ...extra,
});

function LocationPicker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  useEffect(() => {
    if (position) map.flyTo(position, 14);
  }, [position, map]);

  return position ? (
    <Marker
      position={position}
      icon={redIcon}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const newPos = e.target.getLatLng();
          setPosition(newPos);
        },
      }}
    />
  ) : null;
}

export default function Biography() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [workOptions, setWorkOptions] = useState({
    studio: false,
    travel: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCurrency, setLoadingCurrency] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [openCountries, setOpenCountries] = useState(false);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const extractErrorMessage = (data) => {
    if (!data) return "Failed to save biography.";
    if (typeof data === "string") return data;
    if (typeof data.detail === "string" && data.detail.trim())
      return data.detail;
    if (typeof data.message === "string" && data.message.trim())
      return data.message;
    if (typeof data.error === "string" && data.error.trim()) return data.error;

    for (const value of Object.values(data)) {
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "string"
      ) {
        return value[0];
      }
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "Failed to save biography.";
  };
  const token = useMemo(() => {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    );
  }, []);

  const getNumberLengthRange = (countryCode) => {
    try {
      const example = getExampleNumber(countryCode, metadata);
      if (!example) return null;

      const digits = example.nationalNumber.length;
      return {
        min: digits - 1,
        max: digits + 1,
        example: example.nationalNumber,
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCurrency(false);
        setOpenCountries(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (data?.country_code) {
          setSelectedCountry(data.country_code);
          setCountryCode("+" + getCountryCallingCode(data.country_code));
        }
      } catch (err) {
        console.warn("Не удалось определить страну, по умолчанию US", err);
      }
    };
    detectCountry();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setPosition({ lat: latitude, lng: longitude });
        },
        (err) => {
          console.warn("Не удалось получить геолокацию:", err);
          setPosition({ lat: 48.8584, lng: 2.2945 });
        },
      );
    }
  }, []);

  useEffect(() => {
    const fetchAddress = async () => {
      if (!position) return;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${position.lat}&lon=${position.lng}&format=json`,
        );
        const data = await res.json();
        setAddress(data.display_name || "");
        setCity(
          data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            "",
        );
      } catch (err) {
        console.warn("Не удалось определить адрес:", err);
      }
    };
    fetchAddress();
  }, [position]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrency(true);
      try {
        if (!token) throw new Error("Missing token");

        const res = await fetch(`${BASE_URL}/service/currencies/`, {
          headers: buildHeaders(token),
        });

        if (!res.ok)
          throw new Error(`Failed to load currencies (${res.status})`);

        const data = await res.json();

        setCurrencies(
          data.map((item) => ({
            code: item.short_name,
            name: item.short_name,
            flag: item.icon || "",
          })),
        );
        setCurrency(data[0]?.short_name || "");
      } catch (err) {
        console.error("Currency load error:", err);
        toast.error("Could not load currencies, using fallback list.");
        setCurrencies([
          { code: "USD", name: "USD ($)", flag: "https://flagcdn.com/us.svg" },
          { code: "EUR", name: "EUR (€)", flag: "https://flagcdn.com/eu.svg" },
          { code: "GBP", name: "GBP (£)", flag: "https://flagcdn.com/gb.svg" },
        ]);
        setCurrency("USD");
      } finally {
        setLoadingCurrency(false);
      }
    };

    fetchCurrencies();
  }, [token]);

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d+\-\s]/g, "");
    setPhone(cleaned);

    const parsed = parsePhoneNumberFromString(cleaned, selectedCountry);
    const lengthInfo = getNumberLengthRange(selectedCountry);

    if (!parsed) {
      setErrors((prev) => ({
        ...prev,
        phone: "Please enter a valid phone number.",
      }));
      return;
    }

    const numLength = parsed.nationalNumber.length;

    if (lengthInfo) {
      if (numLength < lengthInfo.min) {
        setErrors((prev) => ({
          ...prev,
          phone: `Too short — expected about ${lengthInfo.min}-${lengthInfo.max} digits for ${selectedCountry}.`,
        }));
        return;
      }

      if (numLength > lengthInfo.max) {
        setErrors((prev) => ({
          ...prev,
          phone: `Too long — expected about ${lengthInfo.min}-${lengthInfo.max} digits for ${selectedCountry}.`,
        }));
        return;
      }
    }

    if (!parsed.isPossible()) {
      setErrors((prev) => ({
        ...prev,
        phone: "Phone number length is invalid for this country.",
      }));
      return;
    }

    if (!parsed.isValid()) {
      setErrors((prev) => ({
        ...prev,
        phone: "This phone number format is invalid.",
      }));
      return;
    }

    setErrors((prev) => {
      const newErr = { ...prev };
      delete newErr.phone;
      return newErr;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!fullName.trim() || fullName.length < 2)
      newErrors.fullName = "Full name is required (min 2 characters).";

    const parsed = parsePhoneNumberFromString(
      phone.replace(/[\s-]/g, ""),
      selectedCountry,
    );

    if (!parsed) newErrors.phone = "Please enter a valid phone number.";
    else if (!parsed.isPossible())
      newErrors.phone = "Phone number length is invalid for this country.";
    else if (!parsed.isValid())
      newErrors.phone = "Please enter a valid phone number format.";

    if (!currency) newErrors.currency = "Please select a currency.";
    if (!workOptions.studio && !workOptions.travel)
      newErrors.work = "Please choose at least one work option.";
    if (!position)
      newErrors.location = "Please select your location on the map.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the highlighted errors.");
      return false;
    }
    return true;
  };

  const handleCheckboxChange = (option) => {
    setWorkOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);
    const user_status = localStorage.getItem("user_status") || "individual";

    const sanitizedPhone = phone.replace(/[^\d+]/g, "");
    const parsed = parsePhoneNumberFromString(sanitizedPhone, selectedCountry);
    const normalizedPhone = parsed?.number || sanitizedPhone;

    const lat = position?.lat || 0;
    const lng = position?.lng || 0;
    const address_url = `https://www.google.com/maps?q=${lat},${lng}`;

    const userTimeZone = getUserTimeZone();

    const body = {
      full_name: fullName,
      company_name: "My Company",
      phone_number: normalizedPhone,
      city: city || "Unknown",
      address: address || `${lat}, ${lng}`,
      address_url,
      currency,
      work_place_ids: [1],
      worker_emails: [],
      // если бекенд принимает timezone из headers — это поле можно убрать,
      // но оставляю чтобы не ломать ваш API, если он ещё читает body:
      timeZone: userTimeZone,
    };

    const getValidToken = async () => {
      let t =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        "";

      if (!t) return null;

      const verifyRes = await fetch(`${BASE_URL}/auth/token/verify/`, {
        method: "POST",
        headers: buildHeaders(null),
        body: JSON.stringify({ token: t }),
      });

      if (verifyRes.ok) return t;

      const refresh =
        localStorage.getItem("refreshToken") ||
        sessionStorage.getItem("refreshToken");

      if (!refresh) return null;

      const refreshRes = await fetch(`${BASE_URL}/auth/token/refresh/`, {
        method: "POST",
        headers: buildHeaders(null),
        body: JSON.stringify({ refresh }),
      });

      const data = await refreshRes.json().catch(() => ({}));
      if (refreshRes.ok && data.access) {
        localStorage.setItem("accessToken", data.access);
        return data.access;
      }

      return null;
    };

    try {
      const t = await getValidToken();
      if (!t) {
        toast.error("Session expired. Please log in again.");
        navigate("/sign-in");
        return;
      }

      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(t),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));

        setErrors((prev) => ({
          ...prev,
          fullName: Array.isArray(data.full_name)
            ? data.full_name[0]
            : prev.fullName,
          phone: Array.isArray(data.phone_number)
            ? data.phone_number[0]
            : prev.phone,
          currency: Array.isArray(data.currency)
            ? data.currency[0]
            : prev.currency,
          location: Array.isArray(data.address)
            ? data.address[0]
            : prev.location,
        }));

        throw new Error(extractErrorMessage(data));
      }

      toast.success("Biography saved successfully!");
      localStorage.setItem("userInfoCompleted", "true");

      localStorage.setItem("user_timezone", userTimeZone);
      localStorage.setItem("user_currency", currency);

      let nextCode = null;
      if (user_status === "individual") nextCode = 214;
      if (user_status === "salon") nextCode = 226;
      if (user_status === "worker") nextCode = 234;

      localStorage.setItem("onboarding_code", nextCode.toString());
      localStorage.setItem("onboarding_completed", "false");

      navigate("/choose-niche", { replace: true });
    } catch (err) {
      console.error("❌ Biography save error:", err);
      toast.error(err.message || "Failed to save biography.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>Biography</h2>
              <p>Tell us about your business</p>
            </div>
          </div>

          <div className="biography-content">
            <div className="biography-form" ref={dropdownRef}>
              <div className="biography-form-group">
                <label>
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Type your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={errors.fullName ? "invalid" : ""}
                />
                {errors.fullName && (
                  <span className="input-error-text">{errors.fullName}</span>
                )}
              </div>

              <div className="biography-form-group">
                <label>
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="phone-field">
                  <div
                    className="phone-prefix"
                    onClick={() => {
                      setOpenCountries(!openCountries);
                      setOpenCurrency(false);
                    }}
                  >
                    {React.createElement(flags[selectedCountry] || flags.US, {
                      title: selectedCountry,
                      style: { width: 24, height: 18, marginRight: 6 },
                    })}
                    <span className="country-code">{countryCode}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M7 10l5 5 5-5"
                        stroke="#9A99AB"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <input
                    type="tel"
                    inputMode="tel"
                    pattern="[0-9+\-\s]*"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={`phone-input ${errors.phone ? "invalid" : ""}`}
                  />

                  {openCountries && (
                    <div className="country-dropdown">
                      {getCountries().map((c) => (
                        <div
                          key={c}
                          className="country-item"
                          onClick={() => {
                            setSelectedCountry(c);
                            setCountryCode("+" + getCountryCallingCode(c));
                            setOpenCountries(false);
                          }}
                        >
                          {React.createElement(flags[c] || flags.US, {
                            title: c,
                            style: { width: 20, height: 14, marginRight: 8 },
                          })}
                          <span className="country-name">{c}</span>
                          <span className="country-dial">
                            +{getCountryCallingCode(c)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.phone && (
                  <span className="input-error-text">{errors.phone}</span>
                )}
              </div>

              <div className="biography-form-group biography-form-group-currency">
                <label>
                  Currency <span className="text-red-500">*</span>
                </label>
                <div
                  className={`currency-field ${errors.currency ? "invalid" : ""}`}
                  onClick={() => {
                    if (!loadingCurrency) {
                      setOpenCurrency(!openCurrency);
                      setOpenCountries(false);
                    }
                  }}
                >
                  <span className="currency-display">
                    {loadingCurrency ? (
                      "Loading..."
                    ) : (
                      <>
                        {currencies.find((c) => c.code === currency)?.flag && (
                          <img
                            src={
                              currencies.find((c) => c.code === currency)?.flag
                            }
                            alt={currency}
                            className="currency-flag"
                            style={{
                              width: 20,
                              height: 14,
                              marginRight: 6,
                              objectFit: "contain",
                            }}
                          />
                        )}
                        {currency || "Select currency"}
                      </>
                    )}
                  </span>

                  <svg
                    className={`currency-down ${openCurrency ? "open" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M7 10l5 5 5-5"
                      stroke="#9A99AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {openCurrency && currencies.length > 0 && (
                  <div className="currency-menu">
                    {currencies.map((item) => (
                      <div
                        key={item.code}
                        className="currency-item"
                        onClick={() => {
                          setCurrency(item.code);
                          setOpenCurrency(false);
                        }}
                      >
                        {item.flag && (
                          <img
                            src={item.flag}
                            alt={item.name}
                            className="currency-flag"
                          />
                        )}
                        <span className="currency-name">{item.name}</span>
                        <div
                          className={`currency-radio ${currency === item.code ? "selected" : ""}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {errors.currency && (
                  <span className="input-error-text">{errors.currency}</span>
                )}
              </div>
            </div>

            <div className="biography-map">
              <label>
                City / Address <span className="text-red-500">*</span>
              </label>
              {position ? (
                <MapContainer
                  center={position}
                  zoom={14}
                  style={{
                    height: 246,
                    width: 381,
                    border: "1px solid #9A99AB",
                    borderRadius: "14px",
                  }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <LocationPicker
                    position={position}
                    setPosition={setPosition}
                  />
                </MapContainer>
              ) : (
                <p className="check-location">
                  <HiOutlineMapPin />
                </p>
              )}
              {errors.location && (
                <span className="input-error-text">{errors.location}</span>
              )}
            </div>
          </div>

          <div className="biography-work">
            <label className="biography-work-title">
              Where do you work? <span className="text-red-500">*</span>
            </label>
            <div
              className={`biography-checkbox-group ${errors.work ? "invalid" : ""}`}
            >
              <label>
                <input
                  type="checkbox"
                  checked={workOptions.studio}
                  onChange={() =>
                    setWorkOptions((p) => ({ ...p, studio: !p.studio }))
                  }
                />
                <span>
                  I work at my own place or studio, whether I own or rent it,
                  and/or with other professionals
                </span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={workOptions.travel}
                  onChange={() =>
                    setWorkOptions((p) => ({ ...p, travel: !p.travel }))
                  }
                />
                <span>
                  I travel to my clients location to provide services.
                </span>
              </label>
            </div>
            {errors.work && (
              <span className="input-error-text">{errors.work}</span>
            )}
          </div>

          {errors.general && (
            <p className="biography-error-text">{errors.general}</p>
          )}

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn"
              onClick={handleNext}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? "Saving..." : "Next"}
            </motion.button>
            <span className="biography-step">Step 1/5</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}
